import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router'
import {
  JournalEntryType,
  journalTitle,
  useDeleteJournal,
  useGetJournal,
  useUpdateJournal,
} from '@zendia/frontend-shared'
import {
  ChevronBackward,
  MoreHoriz,
  Edit,
  Delete,
} from '@nine-thirty-five/material-symbols-react/rounded'
import { axiosRequest } from '../../../../utilities/axiosClient'
import { FullView, FullViewContent } from '../../../../components/Modal'
import { ActionButton } from '../../../../components/Button'
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/react'
import { useMemo, useState } from 'react'
import { JournalViewImages } from '../../../../components/Journal/JournalViewImages'
import { JournalImageLightbox } from '../../../../components/Journal/JournalImageLightbox'
import { LabelSelector } from '../../../../components/Input'

export const Route = createFileRoute('/_authenticated/journal/$journalId/detail')({
  component: RouteComponent,
})

function RouteComponent() {
  const { journalId } = Route.useParams()
  const router = useRouter()
  const navigate = useNavigate()
  const { data: journalData } = useGetJournal(axiosRequest, journalId)
  const { mutate: deleteJournal } = useDeleteJournal(axiosRequest)
  const { mutate: updateJournal } = useUpdateJournal(axiosRequest)
  const [activeImageIndex, setActiveImageIndex] = useState<number | null>(null)

  const journalView = useMemo(() => {
    return {
      formattedTitle: journalTitle(
        journalData?.data?.title || '',
        journalData?.data?.suggestedTitle,
      ),
      titleTime: journalData?.data?.createdAt
        ? new Date(journalData.data.createdAt).toLocaleString('default', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })
        : 'Unknown date',
      createdTime: journalData?.data?.createdAt
        ? new Date(journalData.data.createdAt).toLocaleString('default', {
            hour: '2-digit',
            minute: '2-digit', //.toFormat('hh:mm a')
          })
        : 'Unknown date',
      journalTextEntry:
        journalData?.data?.entries?.find((entry) => entry.type === JournalEntryType.Text)?.value ||
        '',
      journalImages:
        journalData?.data?.entries?.filter((entry) => entry.type === JournalEntryType.Photo) || [],
    }
  }, [journalData])

  const onBack = () => {
    if (router.history.canGoBack()) {
      router.history.back()
    } else {
      router.history.replace('/journal')
    }
  }

  const handleEdit = () => {
    navigate({ to: '/journal/$journalId/edit', params: { journalId } })
  }

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this journal?')) {
      deleteJournal(journalId, {
        onSuccess: () => {
          router.history.back()
        },
      })
    }
  }

  const onLabelsChange = (labels: string[]) => {
    updateJournal({ journalId, labels })
  }

  const overflowMenu = () => {
    return (
      <Menu>
        <MenuButton className="size-9 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center bg-gray-100/70 dark:bg-slate-700/70 text-gray-600 dark:text-gray-300">
          <MoreHoriz className="m-auto" />
        </MenuButton>

        <MenuItems
          transition
          anchor="bottom end"
          className="w-26 origin-top-right rounded-xl border border-white/60 bg-white/60 dark:border-neutral-900/25 dark:bg-neutral-900/50 shadow-xl shadow-gray-800/50 backdrop-blur-lg p-1 text-sm/6 text-gray-700 dark:text-gray-300 transition duration-100 ease-out [--anchor-gap:--spacing(1)] focus:outline-none data-closed:scale-95 data-closed:opacity-0"
        >
          <MenuItem>
            <button
              className="group flex w-full items-center gap-2 rounded-lg px-3 py-1.5 data-focus:bg-white/10"
              onClick={handleEdit}
            >
              <Edit className="size-4" />
              Edit
            </button>
          </MenuItem>
          <MenuItem>
            <button
              className="group flex w-full items-center gap-2 rounded-lg px-3 py-1.5 data-focus:bg-white/10 text-red-600 dark:text-red-400"
              onClick={handleDelete}
            >
              <Delete className="size-4" />
              Delete
            </button>
          </MenuItem>
        </MenuItems>
      </Menu>
    )
  }

  return (
    <>
      <FullView
        leftAction={
          <ActionButton
            onClick={onBack}
            label="Discard"
            icon={<ChevronBackward className="size-6" />}
          />
        }
        centerContent={journalView.titleTime}
        rightAction={overflowMenu()}
      >
        <FullViewContent>
          <div className="flex space-x-4 text-sm">
            <div>{journalView.createdTime}</div>
            {/* {journalData?.data?.weather && <div>{journalData.data.weather}</div>}
          {journalData?.data?.location && <div>{journalData.data.location}</div>}
          {journalData?.data?.emotion && <div>{journalData.data.emotion}</div>} */}
          </div>
          <div className="mt-2 mb-3">
            <div className="font-serif font-bold text-3xl">{journalView.formattedTitle}</div>
          </div>
          {journalData?.data && (
            <LabelSelector
              selectedLabels={journalData.data.labels}
              onChange={onLabelsChange}
              className="mt-3 mb-5"
            />
          )}
          {journalView.journalImages.length > 0 && (
            <JournalViewImages
              journalImages={journalView.journalImages}
              onImageClick={setActiveImageIndex}
            />
          )}
          <div className="tracking-tight">{journalView.journalTextEntry}</div>
        </FullViewContent>
      </FullView>
      {activeImageIndex !== null && (
        <JournalImageLightbox
          images={journalView.journalImages}
          activeIndex={activeImageIndex}
          onClose={() => setActiveImageIndex(null)}
          onNavigate={setActiveImageIndex}
        />
      )}
    </>
  )
}
